import React, { useEffect, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUp, CheckCircle2, Compass, ExternalLink, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { SEO_PAGE_MAP, SEO_PAGES } from '../lib/seoPages';
import { buildTemplateSignupPath } from '../lib/profileTemplates';

const SITE_URL = 'https://linkit-tan.vercel.app';

const getSeoOgImageUrl = (slug) => `${SITE_URL}/og/seo-${slug}.jpg`;

const seoThemeClassFor = (slug) => `seo-theme-${slug || 'default'}`;

const relatedFor = (activeSlug) => {
  const activePage = SEO_PAGE_MAP[activeSlug];
  const others = SEO_PAGES.filter((page) => page.slug !== activeSlug);
  if (!activePage) return others.slice(0, 4);

  // Prioritize pages from the same group first, then fill with other groups
  const sameGroup = others.filter((page) => page.group === activePage.group);
  const otherGroups = others.filter((page) => page.group !== activePage.group);
  return [...sameGroup, ...otherGroups].slice(0, 4);
};

const updateMeta = (selector, attr, value) => {
  if (!value) return;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    if (selector.includes('name=')) element.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '');
    if (selector.includes('property=')) element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
    if (selector.includes('rel=')) element.setAttribute('rel', selector.match(/rel="([^"]+)"/)?.[1] || '');
    document.head.appendChild(element);
  }
  element.setAttribute(attr, value);
};

const TEMPLATE_DETAILS = {
  'artist-link-in-bio-template': {
    handle: '@studio.mira',
    name: 'Mira Studio',
    accent: 'bg-edge-coral',
    primaryAction: 'Commission slots open',
    links: ['Portfolio', 'Print shop', 'Commission form', 'Instagram', 'Newsletter'],
    checklist: ['Upload recent work', 'Add commission or shop link', 'Connect QR for events'],
  },
  'developer-link-in-bio-template': {
    handle: '@shipwithdev',
    name: 'Dev Launch',
    accent: 'bg-edge-coral',
    primaryAction: 'Try the live demo',
    links: ['Live app', 'GitHub', 'API docs', 'Changelog', 'Contact'],
    checklist: ['Pin active project', 'Separate docs from demos', 'Add launch or waitlist CTA'],
  },
  'musician-link-in-bio-template': {
    handle: '@northline',
    name: 'Northline',
    accent: 'bg-edge-pink',
    primaryAction: 'Stream the new single',
    links: ['Latest release', 'Tour dates', 'Merch', 'YouTube', 'Fan updates'],
    checklist: ['Feature the latest release', 'Add ticket or merch links', 'Use one QR on posters'],
  },
  'shop-link-in-bio-template': {
    handle: '@smallbatch',
    name: 'Small Batch Shop',
    accent: 'bg-edge-amber',
    primaryAction: 'Shop the new drop',
    links: ['Featured product', 'Bundles', 'Digital downloads', 'Reviews', 'Support'],
    checklist: ['Put best offer first', 'Route checkout clearly', 'Track product clicks'],
  },
  'coach-link-in-bio-template': {
    handle: '@clearpath',
    name: 'Clearpath Coaching',
    accent: 'bg-edge-pink',
    primaryAction: 'Book a discovery call',
    links: ['Book a call', 'Programs', 'Client proof', 'Free resource', 'Newsletter'],
    checklist: ['Choose one conversion goal', 'Add proof before extras', 'Rotate campaign links'],
  },
  'portfolio-link-in-bio-template': {
    handle: '@casework',
    name: 'Casework Portfolio',
    accent: 'bg-edge-umber',
    primaryAction: 'View selected work',
    links: ['Selected work', 'Resume', 'Case studies', 'Writing', 'Contact'],
    checklist: ['Lead with best work', 'Keep contact visible', 'Add resume or booking path'],
  },
};

const SeoPage = ({ slug: routeSlug }) => {
  const { slug: paramSlug } = useParams();
  const slug = routeSlug || paramSlug;
  const page = SEO_PAGE_MAP[slug];

  const related = useMemo(() => relatedFor(slug), [slug]);
  const secondaryHref = page?.secondaryHref || '/discover';

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    if (!page) return;

    const canonical = `${SITE_URL}/${page.slug}`;
    const ogImage = getSeoOgImageUrl(page.slug);
    document.title = `${page.eyebrow}: ${page.title} | Linkit`;
    updateMeta('meta[name="description"]', 'content', page.description);
    updateMeta('link[rel="canonical"]', 'href', canonical);
    updateMeta('meta[property="og:title"]', 'content', `${page.title} | Linkit`);
    updateMeta('meta[property="og:description"]', 'content', page.description);
    updateMeta('meta[property="og:type"]', 'content', 'website');
    updateMeta('meta[property="og:url"]', 'content', canonical);
    updateMeta('meta[property="og:image"]', 'content', ogImage);
    updateMeta('meta[property="og:image:width"]', 'content', '1200');
    updateMeta('meta[property="og:image:height"]', 'content', '630');
    updateMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', 'content', `${page.title} | Linkit`);
    updateMeta('meta[name="twitter:description"]', 'content', page.description);
    updateMeta('meta[name="twitter:image"]', 'content', ogImage);

    const schemaId = 'seo-page-schema';
    let schema = document.getElementById(schemaId);
    if (!schema) {
      schema = document.createElement('script');
      schema.id = schemaId;
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.title,
      description: page.description,
      url: canonical,
      about: page.keywords,
      mainEntity: page.faqs.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    });
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const seoThemeClass = seoThemeClassFor(page.slug);

  if (page.group === 'Template') {
    const template = TEMPLATE_DETAILS[page.slug] || TEMPLATE_DETAILS['portfolio-link-in-bio-template'];
    const templateLinks = related.filter((item) => item.group === 'Template');
    const templateSignupPath = buildTemplateSignupPath(page.slug);

    return (
      <main className={`linkit-owned-page landing-page seo-nature-page ${seoThemeClass} min-h-screen bg-edge-cream vibrant-grunge recycled-paper-overlay text-edge-umber selection:bg-edge-coral/30`}>
        <section className="relative overflow-hidden px-5 pb-20 pt-12 md:px-10 md:pb-28 md:pt-16">
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-edge-pink/10 blur-[120px]" />
          <div className="relative z-10 mx-auto max-w-7xl">
            <Link to="/" className="inline-flex items-center gap-3 text-edge-umber transition-colors hover:text-edge-pink">
              <span className="flex h-9 w-9 items-center justify-center border-2 border-edge-umber bg-edge-coral text-sm font-black text-edge-umber">
                L
              </span>
              <span className="linkit-product-wordmark text-xl">linkit</span>
            </Link>

            <div className="mt-14 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 border-2 border-edge-umber bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] shadow-brutalist">
                  <Sparkles size={14} fill="currentColor" className="text-edge-pink" />
                  {page.eyebrow}
                </div>
                <div className="space-y-5">
                  <h1 className="max-w-4xl text-4xl font-black uppercase leading-[0.95] tracking-tight text-edge-umber md:text-6xl lg:text-7xl">
                    {page.title}
                  </h1>
                  <p className="max-w-2xl text-lg font-bold leading-relaxed text-edge-umber/68 md:text-2xl">
                    {page.description}
                  </p>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Link to={templateSignupPath} className="glass-pill inline-flex items-center justify-center gap-3 px-7 py-4 text-xs font-black uppercase tracking-widest text-white transition-transform hover:translate-x-[2px] hover:translate-y-[2px] bg-edge-pink hover:bg-edge-pink/80">
                    {page.primaryCta}
                    <ArrowRight size={16} />
                  </Link>
                  <Link to={secondaryHref} className="glass-pill inline-flex items-center justify-center gap-3 px-7 py-4 text-xs font-black uppercase tracking-widest text-edge-umber transition-transform hover:translate-x-[2px] hover:translate-y-[2px]">
                    {page.secondaryCta}
                    <Compass size={16} />
                  </Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {page.keywords.map((keyword) => (
                    <span key={keyword} className="glass-pill px-4 py-2 text-[10px] font-black uppercase tracking-widest text-edge-umber/55">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              <aside className="glass-surface-strong p-5 md:p-8">
                <div className="mx-auto max-w-md border-2 border-edge-umber bg-edge-cream p-5 shadow-brutalist">
                  <div className="flex items-center gap-4 border-b-2 border-edge-umber/10 pb-5">
                    <div className={`flex h-16 w-16 items-center justify-center border-2 border-edge-umber ${template.accent} text-2xl font-black text-white`}>
                      {template.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-2xl font-black leading-none text-edge-umber">{template.name}</p>
                      <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-edge-umber/45">{template.handle}</p>
                    </div>
                  </div>
                  <div className="py-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-edge-pink">Primary action</p>
                    <p className="mt-2 text-xl font-black leading-tight text-edge-umber">{template.primaryAction}</p>
                  </div>
                  <div className="grid gap-3">
                    {template.links.map((link, index) => (
                      <div key={link} className="flex items-center justify-between border-2 border-edge-umber bg-white px-4 py-3">
                        <span className="text-xs font-black uppercase tracking-widest text-edge-umber">{link}</span>
                        {index === 0 ? <ArrowRight size={14} className="text-edge-pink" /> : <CheckCircle2 size={14} className="text-edge-umber/35" />}
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-6 px-5 py-12 md:px-10 lg:grid-cols-3">
          {page.sections.map((section, index) => (
            <article key={section.title} className="border-2 border-edge-umber bg-white p-6 shadow-brutalist md:p-8">
              <p className="mb-8 text-[10px] font-black uppercase tracking-[0.28em] text-edge-pink">Included 0{index + 1}</p>
              <h2 className="mb-4 text-2xl font-black uppercase leading-none tracking-tight text-edge-umber">
                {section.title}
              </h2>
              <p className="text-base font-bold leading-relaxed text-edge-umber/65">
                {section.body}
              </p>
            </article>
          ))}
        </section>

        <section className="mx-auto grid max-w-7xl gap-8 px-5 py-16 md:px-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div>
            <p className="mb-4 text-[10px] font-black uppercase tracking-[0.28em] text-edge-pink">Setup checklist</p>
            <h2 className="text-3xl font-black uppercase leading-none tracking-tight text-edge-umber md:text-5xl">
              Build from a real structure, not a blank page.
            </h2>
          </div>
          <div className="grid gap-4">
            {template.checklist.map((item) => (
              <div key={item} className="flex items-center gap-4 border-2 border-edge-umber bg-white p-5 shadow-brutalist">
                <CheckCircle2 size={20} className="shrink-0 text-edge-pink" />
                <p className="text-sm font-black uppercase tracking-widest text-edge-umber/75">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-28 md:px-10">
          <div className="border-2 border-edge-umber bg-edge-umber p-8 text-white shadow-brutalist-pink md:p-10">
            <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
              <div>
                <p className="mb-4 text-[10px] font-black uppercase tracking-[0.28em] text-edge-coral">More templates</p>
                <h2 className="text-3xl font-black uppercase leading-none tracking-tight md:text-5xl">
                  Pick the profile shape that fits the next visitor action.
                </h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {templateLinks.map((item) => (
                  <Link key={item.slug} to={`/${item.slug}`} className="seo-related-card group p-4 transition-transform hover:translate-x-[2px] hover:translate-y-[2px]">
                    <p className="seo-related-kicker text-[9px] font-black uppercase tracking-widest">{item.group}</p>
                    <p className="seo-related-title mt-2 flex items-start justify-between gap-3 text-sm font-black leading-tight">
                      {item.eyebrow}
                      <ExternalLink size={14} className="shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </p>
                    <p className="seo-related-copy mt-2 text-xs font-bold">{item.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <footer className="mt-16 border-t-4 border-edge-umber/5 pt-10">
            <Link to="/" className="inline-flex items-center gap-3 text-edge-umber/45 hover:text-edge-pink transition-colors">
              <span className="flex h-7 w-7 items-center justify-center bg-edge-umber text-xs font-black text-white">
                L
              </span>
              <span className="linkit-product-wordmark text-sm">linkit</span>
            </Link>
          </footer>
        </section>
      </main>
    );
  }

  return (
    <main className={`linkit-owned-page landing-page seo-nature-page ${seoThemeClass} min-h-screen bg-edge-cream vibrant-grunge recycled-paper-overlay text-edge-umber selection:bg-edge-coral/30`}>
      <section className="relative overflow-hidden px-5 pb-24 pt-12 md:px-10 md:pb-32 md:pt-16">
        <div className="absolute left-1/2 top-0 h-80 w-[52rem] -translate-x-1/2 rounded-full bg-edge-coral/15 blur-[120px]" />
        <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-edge-pink/10 blur-[120px]" />

        <div className="relative z-10 mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="space-y-8">
            <Link to="/" className="inline-flex items-center gap-3 text-edge-umber transition-colors hover:text-edge-pink">
              <span className="flex h-9 w-9 items-center justify-center border-2 border-edge-umber bg-edge-coral text-sm font-black text-edge-umber">
                L
              </span>
              <span className="linkit-product-wordmark text-xl">linkit</span>
            </Link>

            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 border-2 border-edge-umber bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] shadow-brutalist">
                <Search size={14} />
                {page.eyebrow}
              </div>
              <h1 className="max-w-4xl text-4xl font-black uppercase leading-[0.95] tracking-tight text-edge-umber md:text-6xl lg:text-7xl">
                {page.title}
              </h1>
              <p className="max-w-3xl text-lg font-bold leading-relaxed text-edge-umber/68 md:text-2xl">
                {page.description}
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/login?signup=true" className="inline-flex items-center justify-center gap-3 border-2 border-edge-umber bg-edge-pink px-7 py-4 text-xs font-black uppercase tracking-widest text-white shadow-brutalist-amber transition-transform hover:translate-x-[2px] hover:translate-y-[2px]">
                {page.primaryCta}
                <ArrowRight size={16} />
              </Link>
              <Link to={secondaryHref} className="inline-flex items-center justify-center gap-3 border-2 border-edge-umber bg-white px-7 py-4 text-xs font-black uppercase tracking-widest text-edge-umber shadow-brutalist transition-transform hover:translate-x-[2px] hover:translate-y-[2px]">
                {page.secondaryCta}
                <Compass size={16} />
              </Link>
            </div>

            <div className="flex flex-wrap gap-2">
              {page.keywords.map((keyword) => (
                <span key={keyword} className="rounded-full border border-edge-umber/15 bg-white/70 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-edge-umber/55">
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          <aside className="border-2 border-edge-umber bg-white p-6 shadow-brutalist-pink md:p-8">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b-2 border-edge-umber/10 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center border-2 border-edge-umber bg-edge-coral text-edge-umber">
                    <Sparkles size={18} fill="currentColor" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-edge-umber/40">Search Intent</p>
                    <p className="text-sm font-black text-edge-umber">Creator research</p>
                  </div>
                </div>
                <ShieldCheck className="text-edge-pink" size={22} />
              </div>

              <p className="text-xl font-black leading-tight text-edge-umber">{page.intent}</p>

              <div className="grid gap-3">
                {['Public profile', 'Shop cards', 'Subscribers', 'Analytics', 'QR Studio'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3 rounded-xl bg-edge-cream px-4 py-3">
                    <CheckCircle2 size={16} className="text-edge-pink" />
                    <span className="text-xs font-black uppercase tracking-widest text-edge-umber/75">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-5 py-12 md:px-10 lg:grid-cols-3">
        {page.sections.map((section, index) => (
          <article key={section.title} className="border-2 border-edge-umber bg-white p-6 shadow-brutalist md:p-8">
            <p className="mb-8 text-[10px] font-black uppercase tracking-[0.28em] text-edge-pink">0{index + 1}</p>
            <h2 className="mb-4 text-2xl font-black uppercase leading-none tracking-tight text-edge-umber">
              {section.title}
            </h2>
            <p className="text-base font-bold leading-relaxed text-edge-umber/65">
              {section.body}
            </p>
          </article>
        ))}
      </section>

      <section className="mx-auto max-w-5xl px-5 py-20 md:px-10">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <p className="mb-3 text-[10px] font-black uppercase tracking-[0.28em] text-edge-pink">FAQ</p>
            <h2 className="text-3xl font-black uppercase leading-none tracking-tight text-edge-umber md:text-5xl">
              Common questions before choosing a bio link tool
            </h2>
          </div>
        </div>
        <div className="space-y-4">
          {page.faqs.map(([question, answer]) => (
            <article key={question} className="border-2 border-edge-umber bg-white p-5 shadow-brutalist">
              <h3 className="text-lg font-black text-edge-umber">{question}</h3>
              <p className="mt-2 text-sm font-bold leading-relaxed text-edge-umber/65">{answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 md:px-10">
        <div className="border-2 border-edge-umber bg-edge-umber p-8 text-white shadow-brutalist-pink md:p-10">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <div>
              <p className="mb-4 text-[10px] font-black uppercase tracking-[0.28em] text-edge-coral">More guides</p>
              <h2 className="text-3xl font-black uppercase leading-none tracking-tight md:text-5xl">
                Explore more ways to use Linkit
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {related.map((item) => (
                <Link key={item.slug} to={`/${item.slug}`} className="seo-related-card group p-4 transition-transform hover:translate-x-[2px] hover:translate-y-[2px]">
                  <p className="seo-related-kicker text-[9px] font-black uppercase tracking-widest">{item.group}</p>
                  <p className="seo-related-title mt-2 flex items-start justify-between gap-3 text-sm font-black leading-tight">
                    {item.eyebrow}
                    <ExternalLink size={14} className="shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </p>
                  <p className="seo-related-copy mt-2 text-xs font-bold">{item.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <footer className="mt-16 flex flex-col gap-6 border-t-4 border-edge-umber/5 pt-10 md:flex-row md:items-center md:justify-between">
          <Link to="/" className="inline-flex items-center gap-3 text-edge-umber/45 hover:text-edge-pink transition-colors">
            <span className="flex h-7 w-7 items-center justify-center bg-edge-umber text-xs font-black text-white">
              L
            </span>
            <span className="linkit-product-wordmark text-sm">linkit</span>
          </Link>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-edge-umber/35 transition-colors hover:bg-edge-umber/5 hover:text-edge-pink"
            aria-label="Back to top"
          >
            <ArrowUp size={16} />
          </button>
        </footer>
      </section>
    </main>
  );
};

export default SeoPage;
